import { useMemo, useState } from "react";
import { ShieldCheck, Star, Share2 } from "lucide-react";
import { ShareCardDialog } from "@/components/site/ShareCard";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useFetch, fileUrl, usePageTitle } from "@/lib/api";
import { DIVISIONS, BRAND, divisionName } from "@/lib/content";
import { PageHero, SectionHeading, EmptyState, Loading, VideoEmbed, Reveal } from "@/components/site/Bits";
import { AthleteCard } from "@/components/site/AthleteCard";

const Featured = ({ a }) => {
  const [sharing, setSharing] = useState(false);
  return (
  <Reveal>
    <div className="relative grid lg:grid-cols-2 overflow-hidden bg-navy border border-gold/60" data-testid="featured-athlete">
      <div className="relative aspect-[4/5] lg:aspect-auto lg:min-h-[520px] bg-pitch">
        {a.photo_url ? <img src={fileUrl(a.photo_url)} alt={a.name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 stripes" />}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-navy/80 hidden lg:block" />
      </div>
      <div className="relative p-8 sm:p-12 flex flex-col justify-center">
        <span className="absolute right-6 top-2 font-display text-[10rem] font-black italic leading-none text-stroke">{a.jersey}</span>
        <p className="eyebrow flex items-center gap-2"><Star className="h-4 w-4 fill-gold" /> Featured Athlete</p>
        <h3 className="mt-4 h-display text-6xl sm:text-7xl">{a.name}</h3>
        <div className="mt-6 flex flex-wrap gap-2">
          {[divisionName(a.division), a.position, a.jersey && `#${a.jersey}`, a.home_org].filter(Boolean).map((t) => <span key={t} className="border border-line px-3 py-1.5 text-xs font-semibold uppercase tracking-wider">{t}</span>)}
        </div>
        {a.bio && <p className="mt-6 text-slate-300">{a.bio}</p>}
        {a.accomplishments && <ul className="mt-6 space-y-2">{a.accomplishments.split("\n").filter(Boolean).map((w) => <li key={w} className="flex gap-2 text-sm"><span className="text-gold">▸</span>{w}</li>)}</ul>}
        <button onClick={() => setSharing(true)} className="btn-gold mt-8 self-start" data-testid="featured-athlete-share-button"><Share2 className="h-4 w-4" /> Share Card</button>
      </div>
    </div>
    {sharing && <ShareCardDialog athlete={a} onClose={() => setSharing(false)} />}
  </Reveal>
  );
};

export default function Athletes() {
  usePageTitle("The Coast's Next Up");
  const { data, loading } = useFetch("/content/athletes");
  const { data: invites } = useFetch("/content/invitations");
  const [div, setDiv] = useState("all");
  const [media, setMedia] = useState(null);
  const athletes = useMemo(() => data || [], [data]);
  const featured = athletes.find((a) => a.featured);
  const weekly = athletes.filter((a) => a.spotlight_week);
  const filtered = useMemo(() => (div === "all" ? athletes : athletes.filter((a) => a.division === div)), [athletes, div]);

  return (
    <div data-testid="athletes-page">
      <PageHero testid="athletes-hero" eyebrow="Athlete Spotlight" image={BRAND.ring}
        title={<>The Coast's<br /><span className="text-gold">Next Up.</span></>}
        sub="Recruiting-style profiles of the athletes representing Florida's Treasure Coast." />

      <section className="bg-pitch py-16 sm:py-24">
        <div className="wrap space-y-20">
          {loading ? <Loading /> : (
            <>
              {featured && <Featured a={featured} />}
              {weekly.length > 0 && (
                <div data-testid="weekly-spotlight">
                  <SectionHeading eyebrow="Weekly Spotlight" title="This Week on the Coast" />
                  <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{weekly.map((a, i) => <AthleteCard key={a.id} athlete={a} index={i} onOpen={setMedia} />)}</div>
                </div>
              )}
              <div>
                <SectionHeading eyebrow="Athlete Board" title="Allstar Profiles" />
                <div className="mt-8 flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="athlete-division-filter">
                  {[{ slug: "all", name: "All" }, ...DIVISIONS].map((d) => (
                    <button key={d.slug} onClick={() => setDiv(d.slug)} data-testid={`athlete-filter-${d.slug}`}
                      className={`shrink-0 px-4 py-2 font-display text-base font-extrabold uppercase italic border transition-colors ${div === d.slug ? "bg-gold text-navy border-gold" : "border-line text-slate-300 hover:border-gold"}`}>
                      {d.name}
                    </button>
                  ))}
                </div>
                <div className="mt-8">
                  {filtered.length ? (
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{filtered.map((a, i) => <AthleteCard key={a.id} athlete={a} index={i} onOpen={setMedia} />)}</div>
                  ) : (
                    <EmptyState testid="athletes-empty" title="Profiles coming soon" body="Athlete profiles will be published after selection, only with parent or guardian permission." />
                  )}
                </div>
              </div>
            </>
          )}

          <div data-testid="invitations-gallery">
            <SectionHeading eyebrow="Official" title={<>Official Allstar <span className="text-gold">Invitations</span></>} sub="Selected invitation graphics from Tri County Allstars. Receiving an invitation does not guarantee a roster spot — all athletes participate in the selection process." />
            <div className="mt-8">
              {invites && invites.length ? (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                  {invites.map((inv) => (
                    <a key={inv.id} href={fileUrl(inv.image_url)} target="_blank" rel="noreferrer" className="group block bg-ink border border-line hover:border-gold overflow-hidden" data-testid={`invitation-${inv.id}`}>
                      <div className="aspect-[4/5] overflow-hidden"><img src={fileUrl(inv.image_url)} alt={inv.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                      <div className="p-4"><p className="font-display text-lg font-extrabold uppercase italic">{inv.athlete_name || inv.title}</p><p className="font-mono text-[10px] uppercase tracking-widest text-gold">{divisionName(inv.division)}</p></div>
                    </a>
                  ))}
                </div>
              ) : <EmptyState testid="invitations-empty" title="Invitations gallery coming soon" />}
            </div>
          </div>

          <div className="flex gap-4 card-navy p-6" data-testid="athlete-privacy-notice">
            <ShieldCheck className="h-6 w-6 text-gold shrink-0" />
            <p className="text-sm text-slate-300">Tri County Allstars only publishes real athlete information and photographs provided or approved by the organization. A parent or guardian's permission is required before any minor's profile or identifying information is displayed publicly.</p>
          </div>
        </div>
      </section>

      <Dialog open={!!media} onOpenChange={(o) => !o && setMedia(null)}>
        <DialogContent className="max-w-3xl bg-pitch border-line p-0 overflow-hidden" data-testid="athlete-media-dialog">
          <DialogTitle className="sr-only">{media?.name} featured media</DialogTitle>
          {media && <VideoEmbed url={media.media_url} testid="athlete-media-player" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
